/**
 * 🖼️ Service de gestion des icônes personnalisées.
 * Permet de téléverser et gérer les icônes personnalisées stockées localement.
 */

export interface CustomIcon {
  id: string;
  name: string;
  key: string;
  url: string;
  color: string;
  fileName: string;
  fileSize: number;
  uploadedAt: string;
  userId?: string;
}

export interface CustomIconCreate {
  name: string;
  key: string;
  file: File | null;
  color?: string;
}

const STORAGE_KEY = 'portfolio_custom_icons';

/**
 * Service principal pour la gestion des icônes personnalisées.
 */
class CustomIconsService {

  /**
   * Récupère toutes les icônes personnalisées depuis le localStorage.
   * (En attente d'une intégration complète avec l'API du backend).
   */
  getCustomIcons(): CustomIcon[] {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) {
        return [];
      }

      const icons: CustomIcon[] = JSON.parse(stored);
      return icons.sort((a, b) => new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime());
    } catch (error) {
      console.error('❌ Erreur chargement icônes personnalisées :', error);
      return [];
    }
  }

  /**
   * Sauvegarde les icônes personnalisées dans le localStorage.
   */
  private saveCustomIcons(icons: CustomIcon[]): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(icons));
    } catch (error) {
      console.error('❌ Erreur sauvegarde icônes personnalisées :', error);
      throw error;
    }
  }

  /**
   * Crée une nouvelle icône personnalisée.
   */
  async createCustomIcon(data: CustomIconCreate): Promise<CustomIcon> {
    try {

      if (!data.file) {
        throw new Error('Fichier manquant');
      }

      // Simulation de téléversement - pour l'instant on utilise une URL temporaire (base64)
      const fileUrl = await this.simulateFileUpload(data.file);
      const newIcon: CustomIcon = {
        id: this.generateId(),
        name: data.name,
        key: data.key,
        url: fileUrl,
        color: data.color || '#2c3e50',
        fileName: data.file.name,
        fileSize: data.file.size,
        uploadedAt: new Date().toISOString(),
        userId: 'current-user' // Sera remplacé par l'ID utilisateur réel de l'API
      };

      const icons = this.getCustomIcons();
      icons.unshift(newIcon);
      this.saveCustomIcons(icons);
      return newIcon;
    } catch (error) {
      console.error('❌ Erreur création icône personnalisée :', error);
      throw error;
    }
  }

  /**
  * Supprime une icône personnalisée.
  */
  deleteCustomIcon(id: string): void {
    try {
      const icons = this.getCustomIcons().filter(icon => icon.id !== id);
      this.saveCustomIcons(icons);
    } catch (error) {
      console.error('❌ Erreur suppression icône personnalisée :', error);
      throw error;
    }
  }

  /**
  * Met à jour une icône personnalisée.
   */
  updateCustomIcon(id: string, updates: Partial<Omit<CustomIcon, 'id' | 'uploadedAt'>>): CustomIcon {
    try {
      const icons = this.getCustomIcons();
      const index = icons.findIndex(icon => icon.id === id);

      if (index === -1) {
        throw new Error(`Icône personnalisée non trouvée : ${id}`);
      }

      const updatedIcon: CustomIcon = {
        ...icons[index],
        ...updates
      };

      icons[index] = updatedIcon;
      this.saveCustomIcons(icons);
      return updatedIcon;
    } catch (error) {
      console.error('❌ Erreur mise à jour icône personnalisée :', error);
      throw error;
    }
  }

  /**
   * Recherche parmi les icônes personnalisées.
   */
  searchCustomIcons(query: string): CustomIcon[] {
    const lowerQuery = query.toLowerCase();
    return this.getCustomIcons().filter(icon =>
      icon.name.toLowerCase().includes(lowerQuery) ||
      icon.key.toLowerCase().includes(lowerQuery)
    );
  }

  /**
   * Vérifie si une clé d'icône existe déjà.
   */
  iconKeyExists(key: string): boolean {
    return this.getCustomIcons().some(icon => icon.key === key);
  }

  /**
   * Génère un ID unique.
   */
  private generateId(): string {
    return 'custom_' + Date.now().toString(36) + Math.random().toString(36).substr(2, 9);
  }

  /**
   * Simule le téléversement de fichier en le convertissant en Base64.
   */
  private async simulateFileUpload(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        resolve(reader.result as string);
      };
      reader.onerror = () => reject(new Error('Erreur lecture fichier'));
      reader.readAsDataURL(file);
    });
  }
}

// Instance unique du service
export const customIconsService = new CustomIconsService();
